/**
 * ISO 4217 currency capability + formatting — shared Hub/Portal SSOT.
 * Mirror of forest.config.currency (Python kernel).
 */

/** System default ISO code — typed as string so default params accept any currency. */
export const DEFAULT_CURRENCY: string = 'EUR';

/** ISO 4217 alpha → numeric (same 12 codes as forest.config.currency). */
export const SUPPORTED_CURRENCIES: Readonly<Record<string, number>> = {
  EUR: 978,
  USD: 840,
  GBP: 826,
  CHF: 756,
  SEK: 752,
  NOK: 578,
  DKK: 208,
  PLN: 985,
  CZK: 203,
  HUF: 348,
  RON: 946,
  BGN: 975,
};

export const SUPPORTED_CURRENCY_CODES = Object.freeze(Object.keys(SUPPORTED_CURRENCIES)) as readonly string[];

const CURRENCY_LOCALES: Record<string, string> = {
  EUR: 'pt-PT',
  GBP: 'en-GB',
  USD: 'en-US',
};

const DEFAULT_LOCALE = 'pt-PT';

const LOCALE_CURRENCIES: Record<string, string> = Object.fromEntries(
  Object.entries(CURRENCY_LOCALES).map(([currency, locale]) => [locale, currency])
);

export function normalizeCurrency(code: string): string {
  return code.trim().toUpperCase();
}

export function isSupportedCurrency(code: string): boolean {
  return normalizeCurrency(code) in SUPPORTED_CURRENCIES;
}

export function currencyToLocale(currency: string): string {
  return CURRENCY_LOCALES[normalizeCurrency(currency)] ?? DEFAULT_LOCALE;
}

/** Reverse of currencyToLocale; unmapped locales fall back to GBP for en-*, else EUR. */
export function localeToCurrency(locale: string): string {
  return LOCALE_CURRENCIES[locale] ?? (locale.toLowerCase().startsWith('en') ? 'GBP' : DEFAULT_CURRENCY);
}

/** Bare currency symbol (e.g. "€", "£") for hand-built compact labels. */
export function currencySymbol(currency: string): string {
  const code = normalizeCurrency(currency);
  const parts = new Intl.NumberFormat(currencyToLocale(code), {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(0);
  return parts.find((part) => part.type === 'currency')?.value ?? code;
}

/** Full currency string with two decimals (e.g. "£1,234.56", "1.234,56 €"). */
export function formatMoney(value: number, currency: string = DEFAULT_CURRENCY): string {
  const code = normalizeCurrency(currency);
  return new Intl.NumberFormat(currencyToLocale(code), {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Compact symbol-prefixed money for dense chart labels — thousands/millions
 * with a `k`/`M` suffix (e.g. "£7.6k", "€1.2M", "£500").
 */
export function formatMoneyCompact(value: number, currency: string = DEFAULT_CURRENCY, decimals = 1): string {
  const symbol = currencySymbol(currency);
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  if (abs >= 1_000_000) return `${sign}${symbol}${(abs / 1_000_000).toFixed(decimals)}M`;
  if (abs >= 1_000) return `${sign}${symbol}${(abs / 1_000).toFixed(decimals)}k`;
  return `${sign}${symbol}${abs.toFixed(0)}`;
}
